import { useQuery } from '@tanstack/react-query'
import { isUndefined } from 'lodash'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useProjectListFilters, useTRPC } from '../../hooks'
import type { Tag } from '../../schemas'

export const useTagFilters = () => {
  const api = useTRPC()

  const {
    filters: { tag_ids: initialSelectedTagIds },
    setTagIds,
  } = useProjectListFilters()

  const tagsQueryOptions = api.shared.tags.list.queryOptions({})
  const { data: tags } = useQuery(tagsQueryOptions)

  const [selectedTags, setSelectedTags] = useState<Tag[]>([])
  const hasInitializedTags = useRef(false)

  useEffect(() => {
    if (initialSelectedTagIds && tags && !hasInitializedTags.current) {
      const initialSelectedTags = initialSelectedTagIds
        .map((tagId) => tags.find((tag) => tag.id === tagId))
        .filter((tag) => !isUndefined(tag))
      setSelectedTags(initialSelectedTags)
      hasInitializedTags.current = true
    }
  }, [initialSelectedTagIds, tags])

  const handleUpdateSelectedTags = useCallback(
    (tagId: string, action: 'add' | 'remove') => {
      // Update inner state
      let newSelectedTags = [...selectedTags]
      if (action === 'add') {
        const tag = tags?.find((tag) => tag.id === tagId)
        if (tag) {
          newSelectedTags.push(tag)
        }
      } else {
        newSelectedTags = newSelectedTags.filter((tag) => tag.id !== tagId)
      }
      setSelectedTags(newSelectedTags)

      // Update URL state
      void setTagIds(newSelectedTags.map((tag) => tag.id))
    },
    [selectedTags, setTagIds, tags]
  )

  const handleClearSelectedTags = useCallback(() => {
    setSelectedTags([])
    void setTagIds(null)
  }, [setTagIds])

  return {
    selectedTags,
    handleUpdateSelectedTags,
    handleClearSelectedTags,
    tags,
  }
}
